import { useCallback, useRef, useState } from 'react';

import {
  AimOutlined,
  EnvironmentOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { TiendaPerfectaStopDetail } from 'models/TiendaPerfecta';

import { GOOGLE_MAP_API_KEY } from 'config/Config';

import {
  STOP_MAP_COLORS,
  STOP_MAP_POINTS,
  StopMapPointKey,
  isSameStartAndFinish,
} from '../utils/stopDetail';

const I18N_BASE = 'scenes.route_report.components.stop_detail';

const MAP_ZOOM = 17;

/**
 * Los mismos marcadores que ya usa `EvidenceMap` en el módulo de KIT. Se
 * reutilizan para no tener dos códigos de color distintos para la misma idea
 * dentro del producto.
 */
const MARKER_ICONS: Record<StopMapPointKey, string> = {
  planned: 'http://maps.google.com/mapfiles/ms/icons/purple-dot.png',
  start: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
  finish: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
};

interface LatLng {
  lat: number;
  lng: number;
}

interface StopMapProps {
  detail: TiendaPerfectaStopDetail;
}

/**
 * Los tres puntos de interés de una parada, con su leyenda.
 *
 * La leyenda enumera SIEMPRE los tres, incluso los que el backend no mandó: uno
 * ausente se muestra en gris y sin acción en vez de desaparecer, porque su
 * ausencia es información —hoy `planned` no llega en ninguna parada— y porque
 * si desapareciera, la leyenda cambiaría de forma entre una parada y otra y ya
 * no se podría leer de un vistazo qué falta.
 *
 * Las coordenadas llegan como STRING con decimales para no perder precisión en
 * el camino: se parsean sólo acá, que es donde hay que pintarlas.
 */
function StopMap({ detail }: StopMapProps) {
  const { t } = useTranslation();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
  });

  const [focused, setFocused] = useState<StopMapPointKey | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const initialCenter = useRef<LatLng | null>(null);

  const { coordinates } = detail.stop;

  const toLatLng = (key: StopMapPointKey): LatLng | null => {
    const point = coordinates[key];
    if (!point) return null;

    const lat = Number(point.lat);
    const lng = Number(point.lng);

    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  };

  const positions = {
    planned: toLatLng('planned'),
    start: toLatLng('start'),
    finish: toLatLng('finish'),
  };

  const sameStartAndFinish = isSameStartAndFinish(detail);

  /* El centro se fija una sola vez y de forma imperativa: si viajara por el
     prop `center`, cada re-render devolvería el mapa a su posición y anularía
     el desplazamiento que hizo el usuario. */
  const center = positions.start ?? positions.finish ?? positions.planned;

  if (initialCenter.current === null && center) {
    initialCenter.current = center;
  }

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    if (initialCenter.current) {
      map.setCenter(initialCenter.current);
      map.setZoom(MAP_ZOOM);
    }
  }, []);

  const focusPoint = (key: StopMapPointKey) => {
    const position = positions[key];
    if (!position) return;

    /* Volver a tocar la pill activa suelta el foco: no hay otra forma de
       deshacer la selección sin un botón extra que no aporta. */
    setFocused((previous) => (previous === key ? null : key));
    mapRef.current?.panTo(position);
    mapRef.current?.setZoom(MAP_ZOOM);
  };

  /* El pie sigue al MISMO punto que el mapa puede centrar. Elegirlo del objeto
     crudo dejaba imprimir una coordenada que no se pudo parsear —y que por eso
     figura como "sin dato" en la leyenda— mientras el mapa está centrado en
     otra. */
  const shownKey: StopMapPointKey | null =
    focused && positions[focused]
      ? focused
      : (['start', 'finish', 'planned'] as StopMapPointKey[]).find(
          (key) => positions[key]
        ) ?? null;

  const shownCoordinate = shownKey ? coordinates[shownKey] : null;

  const legend = (
    <div className="stop-map__legend">
      {STOP_MAP_POINTS.map((key) => {
        const available = Boolean(positions[key]);

        return (
          <button
            type="button"
            key={key}
            className={[
              'stop-map__pill',
              focused === key ? 'stop-map__pill--active' : '',
              available ? '' : 'stop-map__pill--empty',
            ]
              .filter(Boolean)
              .join(' ')}
            style={available ? { color: STOP_MAP_COLORS[key] } : undefined}
            disabled={!available}
            onClick={() => focusPoint(key)}
            title={available ? t(`${I18N_BASE}.focus_point`) : undefined}
          >
            <i className="stop-map__swatch" aria-hidden="true" />
            <span className="stop-map__pill-label">
              {t(`${I18N_BASE}.points.${key}`)}
            </span>
            {available ? (
              <AimOutlined aria-hidden="true" />
            ) : (
              <span className="stop-map__pill-empty">
                {t(`${I18N_BASE}.no_point_data`)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  /* El pie se arma acá arriba, como la leyenda, porque lo comparten el mapa
     cargado y su silueta: si sólo lo tuviera el mapa, aparecería de golpe al
     terminar de cargar y correría todo lo que tiene debajo. */
  const foot = shownCoordinate ? (
    <div className="stop-map__foot">
      <EnvironmentOutlined aria-hidden="true" />
      {/* Se nombra a cuál de los tres puntos pertenecen los números: sin foco
          elegido, la coordenada sola no dice de cuál es. */}
      <span className="stop-map__foot-point">
        {t(`${I18N_BASE}.points.${shownKey}`)}
      </span>
      <span className="route-report-num">
        {shownCoordinate.lat}, {shownCoordinate.lng}
      </span>
      {shownCoordinate.accuracyM && (
        <Tag>
          {t(`${I18N_BASE}.accuracy`, { meters: shownCoordinate.accuracyM })}
        </Tag>
      )}
    </div>
  ) : null;

  /* Una parada sin ningún punto utilizable no tiene nada que centrar. La
     leyenda igual se muestra: es la que explica cuál de los tres falta.

     El texto distingue por qué no hay coordenadas: una parada que todavía no se
     visitó no tiene ninguna, y ésa es la explicación completa; una ya ejecutada
     sin lecturas es otra cosa —el GPS no las dejó— y decirle "no se visitó"
     contradiría el tag "Ejecutada" y las horas que la misma pantalla muestra. */
  if (!center) {
    /* Un texto por estado, y uno neutro para lo que venga después.
       Los tres valores del enum explican la ausencia de maneras distintas
       —meter `in_progress` con `finished` hacía que una parada en curso dijera
       "se ejecutó", en pasado, contra el tag "En curso" de al lado—, y un valor
       que upstream agregue mañana no puede caer en ninguno de ellos: afirmaría
       un estado que el payload no declaró. */
    const EMPTY_MAP_TEXTS: Record<string, string> = {
      pending: 'no_coordinates',
      in_progress: 'no_coordinates_in_progress',
      finished: 'no_coordinates_recorded',
    };

    const emptyKey =
      EMPTY_MAP_TEXTS[detail.stop.status] ?? 'no_coordinates_unknown';

    return (
      <div className="stop-map">
        <div className="stop-map__empty">
          <EnvironmentOutlined />
          <span>{t(`${I18N_BASE}.${emptyKey}`)}</span>
        </div>
        {legend}
      </div>
    );
  }

  /* Un script de Maps que no carga —clave ausente o inválida, red bloqueada—
     dejaba el bloque de carga animándose para siempre, indistinguible de
     "todavía cargando". */
  if (loadError) {
    return (
      <div className="stop-map">
        <div className="stop-map__empty stop-map__empty--error">
          <WarningOutlined />
          <span>{t(`${I18N_BASE}.map_load_error`)}</span>
        </div>
        {legend}
      </div>
    );
  }

  /* La MISMA silueta que ya muestra el bloque de carga del detalle, no la de
     antd. `Skeleton.Image` metía un TERCER estado entre la silueta y el mapa
     —sin leyenda, sin pie y con su icono de imagen en el medio—: con el script
     de Maps ya en caché no se ve nunca, pero en la primera carga de la página
     parpadeaba entre los otros dos. Ahora lo único que cambia al cargar es el
     lienzo. */
  if (!isLoaded) {
    return (
      <div className="stop-map">
        <span className="stop-sk stop-map__canvas-sk" aria-hidden="true" />
        {legend}
        {foot}
      </div>
    );
  }

  return (
    <div className="stop-map">
      <GoogleMap
        zoom={MAP_ZOOM}
        mapContainerClassName="stop-map__canvas"
        onLoad={onMapLoad}
        options={{
          fullscreenControl: true,
          fullscreenControlOptions: {
            position: google.maps.ControlPosition.BOTTOM_RIGHT,
          },
          streetViewControl: false,
          mapTypeControl: false,
        }}
      >
        {STOP_MAP_POINTS.map((key) => {
          const position = positions[key];
          if (!position) return null;

          /* Con inicio y fin en la misma coordenada, dos marcadores quedan
             superpuestos: sólo se puede tocar el de arriba y el de abajo es
             invisible. Se dibuja uno solo y la leyenda sigue teniendo sus dos
             entradas, que es lo que deja ver que son dos lecturas. */
          if (sameStartAndFinish && key === 'finish') return null;

          /* Ese marcador único representa a los DOS puntos, así que toma el
             color de aquel que esté enfocado: elegir "Fin" en la leyenda y ver
             el pin de inicio ahí clavado haría pensar que el foco no hizo
             nada. Sin foco elegido muestra el de llegada, que es el primero de
             los dos en el tiempo. */
          const iconKey =
            sameStartAndFinish && key === 'start' && focused === 'finish'
              ? 'finish'
              : key;

          return (
            <Marker
              key={key}
              position={position}
              icon={{ url: MARKER_ICONS[iconKey] }}
              /* El click sigue al icono, no a la clave del marcador: con inicio
                 y fin en el mismo punto, tocar el pin rojo tenía que apagar el
                 foco de "Fin" y en cambio lo movía a "Inicio", devolviendo el
                 pin a verde. El mapa deshacía la selección del usuario. */
              onClick={() => focusPoint(iconKey)}
            />
          );
        })}
      </GoogleMap>

      {legend}

      {foot}
    </div>
  );
}

export default StopMap;
